import { get, omit } from 'lodash';
import PropTypes from 'prop-types';
import React, { Component, createRef } from 'react';
import ReactPlayer from 'react-player/lazy';

import Image from 'components/ImageV2';
import { IMAGE_RATIOS, IMAGE_WIDTHS } from 'constants/images';
import Cloudinary from 'utils/Cloudinary';
import Environment from 'utils/Environment';

const VideoPlayer = class extends Component {
  constructor(props) {
    super(props);
    this.player = createRef();
    this.state = {
      isReady: false,
    };
    this._needsSafariFix = false;
  }

  get ratio() {
    // Assume ratio is a string like "3:4"
    const ratio = this.props.aspectRatio || IMAGE_RATIOS.R_1x1;
    const [widthRatio, heightRatio] = ratio.split(':').map(Number);
    // Calculate the percentage of height relative to width
    const paddingTop = (heightRatio / widthRatio) * 100;
    return `${paddingTop}%`;
  }

  get currentTime() {
    try {
      return this.player.current.getCurrentTime() || 0;
    } catch (e) {
      return 0;
    }
  }

  get url() {
    let url;

    try {
      url = Cloudinary.buildAutoUploadUrl(this.props.url);
    } catch (error) {
      url = this.props.url;
    }

    return url;
  }

  handleError = (...args) => {
    const { onError } = this.props;

    this.handleSafariBug(...args);

    if (onError) {
      onError(...args);
    }
  };

  handleReady = (...args) => {
    const { onReady } = this.props;
    if (this._needsSafariFix) {
      this.fixSafari();
    }

    this.setState({ isReady: true }, () => {
      if (onReady) {
        onReady(...args);
      }
    });
  };

  fixSafari = () => {
    this.player.current.seekTo(1);
    this._needsSafariFix = false;
  };

  handleSafariBug = (details, e) => {
    // Safari bug where progress bar needs to be nudged on video load
    if (
      Environment.isSafariDesktop &&
      this.currentTime === 0 &&
      get(e, 'details') === 'bufferNudgeOnStall'
    ) {
      if (this.state.isReady) {
        this.fixSafari();
      } else {
        this._needsSafariFix = true;
      }
    }
  };

  handleSafariLoop = details => {
    // Handles Safari bug where videos don't loop properly (https://github.com/CookPete/react-player/issues/496)
    const isAtVideoEnd = get(details, 'played') > 0.95 || get(details, 'played') === 0;

    // If end of the video, simulate loop by rewinding to video start
    if (Environment.isSafariDesktop && this.props.loop && isAtVideoEnd) {
      this.player.current.seekTo(1);
    }
  };

  handleProgress = details => {
    this.handleSafariLoop(details);

    this.props.onProgress(details);
  };

  render() {
    const { isReady } = this.state;
    const remainingProps = omit(this.props, ['url', 'poster', 'aspectRatio']);
    const showBanner = !isReady && this.props.poster;

    const config = this.props.lazy
      ? {
          file: {
            attributes: {
              class: 'Lazy',
              preload: 'none',
            },
          },
        }
      : {};

    if (this.props.lazy) {
      delete remainingProps.playing;
    }

    if (this.props.lazy && this.props.playing) {
      config.file.attributes['data-autoplay'] = '';
    }

    return (
      <div className="VideoPlayer">
        {showBanner && (
          <Image
            src={this.props.poster}
            alt="Video loading banner"
            className="VideoPlayer__banner"
            width={IMAGE_WIDTHS.W_1200}
            ratio={this.props.aspectRatio ? this.props.aspectRatio : IMAGE_RATIOS.R_1x1}
            coverParentNode
          />
        )}
        <ReactPlayer
          style={{ position: 'absolute', top: 0, left: 0 }}
          url={this.url}
          ref={this.player}
          {...remainingProps}
          onError={this.handleError}
          onReady={this.handleReady}
          onProgress={this.handleProgress}
          muted={this.props.muted}
          volume={this.props.muted ? 0 : this.props.volume || 1}
          width="100%"
          height="100%"
          config={{
            file: {
              attributes: {
                style: { width: '100%', height: '100%', objectFit: 'cover' },
              },
            },
          }}
        />

        <style jsx>{`
          .VideoPlayer {
            position: relative;
            padding-bottom: ${this.ratio};
          }
        `}</style>

        <style jsx global>{`
          .VideoPlayer__banner {
            position: absolute;
            top: 0;
            left: 0;
            z-index: 1;
            width: 100%;
            height: 100%;
          }
        `}</style>
      </div>
    );
  }
};

VideoPlayer.propTypes = {
  aspectRatio: PropTypes.string,
  className: PropTypes.string,
  lazy: PropTypes.bool,
  loop: PropTypes.bool,
  muted: PropTypes.bool,
  onError: PropTypes.func,
  onProgress: PropTypes.func,
  onReady: PropTypes.func,
  playing: PropTypes.bool,
  poster: PropTypes.string,
  url: PropTypes.string,
  volume: PropTypes.number,
};

VideoPlayer.defaultProps = {
  loop: false,
  onError: () => {},
  onProgress: () => {},
  onReady: null,
  playing: false,
  poster: null,
  url: null,
  volume: null,
};

export default VideoPlayer;
